import { useMutation, useQuery as useGQLQuery } from '@apollo/client';
import { Button, Icon, Tooltip } from '@mui/material';
import { GridRenderCellParams } from '@mui/x-data-grid';

import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import ENROLL_MUTATION from 'gql/mutations/InternationalOffice/enrollIOCourse.gql';
import UNENROLL_MUTATION from 'gql/mutations/InternationalOffice/unenrollIOCourse.gql';
import ENROLLED_QUERY from 'gql/queries/InternationalOffice/getEnrolledIOCourses.gql';
import QUERY_DEFAULT from 'gql/queries/InternationalOffice/getIOCourses.gql';
import SETTINGS_QUERY from 'gql/queries/InternationalOffice/getIOSettings.gql';

import { useFacultiesQuery } from 'api/hooks/useFacultiesQuery';
import { TypedGridColumns } from 'app/components';
import { DataGrid } from 'app/components/DataGrid';
import { FilterProps } from 'app/components/Filters';
import GQLGuard from 'app/components/GQLGuard';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { Faculty, IOCourse } from 'types/AppTypes';

interface IOCourseListProps {
  showOnlyEnrolled?: boolean;
}

const isActive = (semester: string, isWinter: boolean, isSummer: boolean) => {
  return (semester === 'Winter' && isWinter) || (semester === 'Summer' && isSummer);
};

export const IOCourseStudentList: React.FC<IOCourseListProps> = ({ showOnlyEnrolled }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [enrollIOCourse] = useMutation(ENROLL_MUTATION);
  const [unenrollIOCourse] = useMutation(UNENROLL_MUTATION);
  const [semesterFilterModified, setSemesterFilterModified] = useState(false);

  const QUERY = showOnlyEnrolled ? ENROLLED_QUERY : QUERY_DEFAULT;

  const keywordState = useState();
  const semesterState = useState<string>('');
  const awpmState = useState<boolean>();
  const degreeState = useState<string>('');
  const facultyState = useState('');

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0
  });

  const { data: faculties } = useFacultiesQuery();

  const settingsQuery = useGQLQuery<any, any>(SETTINGS_QUERY, {
    onCompleted: data => {
      if (!semesterFilterModified) {
        semesterState[1](data.ioSettings.semesterType);
      }
    }
  });

  const coursesQuery = useGQLQuery<
    any,
    {
      offset?: number;
      limit?: number;
      filter?: Object;
    }
  >(QUERY, {
    variables: {
      offset: 0,
      limit: 25,
      filter: JSON.stringify({
        ...(settingsQuery?.data?.ioSettings?.semesterType === 'Summer' && { isSummer: true }),
        ...(settingsQuery?.data?.ioSettings?.semesterType === 'Winter' && { isWinter: true })
      })
    },
    fetchPolicy: 'no-cache',
    skip: !settingsQuery.data
  });
  const enrolledCoursesQuery = useGQLQuery<
    any,
    {
      offset?: number;
      limit?: number;
      enrolled?: boolean;
    }
  >(ENROLLED_QUERY, {
    variables: { offset: undefined, limit: undefined, enrolled: true },
    fetchPolicy: 'no-cache'
  });

  const { refetch } = coursesQuery;

  const semester = useMemo(
    () => (settingsQuery.data ? settingsQuery.data.ioSettings.semesterType : undefined),
    [settingsQuery.data]
  );

  const enrolled = useCallback(
    courseId =>
      enrolledCoursesQuery.data?.ioCourses.find(enrolledCourse => enrolledCourse.id === courseId),
    [enrolledCoursesQuery.data]
  );

  const handlePagination = useCallback(
    (event, page: number) => {
      const offset = page * paginationModel.pageSize;
      refetch({ offset: offset });
    },
    [paginationModel.pageSize, refetch]
  );

  const handlePaginationChange = useCallback(pagination => {
    setPaginationModel(pagination);
  }, []);

  const handleEnroll = useCallback(
    async courseId => {
      try {
        await enrollIOCourse({
          variables: { id: courseId },
          refetchQueries: ['GetIOCourses', 'GetEnrolledIOCourses']
        });
        dispatch(
          AlertActions.success(
            t(translations.pages.internationalOffice.courses.alerts.success.enrolledCourse())
          )
        );
      } catch (err) {
        dispatch(
          AlertActions.error(
            t(translations.pages.internationalOffice.courses.alerts.error.enrollingCourse())
          )
        );
      }
    },
    [dispatch, enrollIOCourse, t]
  );

  const handleUnenroll = useCallback(
    async courseId => {
      try {
        await unenrollIOCourse({
          variables: { id: courseId },
          refetchQueries: ['GetIOCourses', 'GetEnrolledIOCourses']
        });
        dispatch(
          AlertActions.success(
            t(translations.pages.internationalOffice.courses.alerts.success.unenrolledCourse())
          )
        );
      } catch (err) {
        dispatch(
          AlertActions.error(
            t(translations.pages.internationalOffice.courses.alerts.error.unenrollingCourse())
          )
        );
      }
    },
    [dispatch, t, unenrollIOCourse]
  );

  const filters = (facultyData: Faculty[], ioFaculties?): FilterProps['filterConfig'] => {
    const facultySelectOptions: [{ label: string; name: string }] = [{ label: '---', name: '' }];
    let faculties = ioFaculties
      ? ioFaculties
      : facultyData
          .map(faculty => {
            if (faculty.courses?.filter(course => !course.isCommunity)?.length) {
              return faculty.name_en || faculty.name;
            }
            return undefined;
          })
          .filter(Boolean) || [];

    faculties
      .sort((a, b) =>
        a?.toLowerCase() > b?.toLowerCase() ? 1 : a?.toLowerCase() < b?.toLowerCase() ? -1 : 0
      )
      .forEach(faculty => {
        facultySelectOptions.push({
          label: faculty,
          name: faculty
        });
      });

    const degreeOptions: { label: string; name: string }[] = [
      { label: '---', name: '' },
      { label: 'Bachelor', name: 'Bachelor' },
      { label: 'Master', name: 'Master' }
    ];

    const semesterOptions = [
      { label: '---', name: '' },
      { label: t(translations.pages.internationalOffice.courses.isSummer()), name: 'Summer' },
      { label: t(translations.pages.internationalOffice.courses.isWinter()), name: 'Winter' }
    ];

    return [
      {
        field: 'keyword',
        label: t(translations.pages.internationalOffice.users.search()),
        stateHandler: keywordState
      },
      {
        field: 'degree',
        label: t(translations.pages.internationalOffice.courses.degree()),
        stateHandler: degreeState,
        select: {
          options: degreeOptions,
          stateHandler: degreeState
        }
      },
      {
        field: 'faculty',
        label: t(translations.pages.internationalOffice.courses.faculty()),
        stateHandler: facultyState,
        select: {
          options: facultySelectOptions,
          stateHandler: facultyState
        }
      },
      {
        field: 'semester',
        label: t(translations.pages.internationalOffice.courses.semester()),
        filterSetter: (value, curFilters) => {
          const filters = { ...curFilters };
          if (filters['isWinter']) delete filters.isWinter;
          if (filters['isSummer']) delete filters.isSummer;
          if (value === 'Summer') filters.isSummer = true;
          if (value === 'Winter') filters.isWinter = true;
          setSemesterFilterModified(true);
          return filters;
        },
        stateHandler: semesterState,
        select: {
          options: semesterOptions,
          stateHandler: semesterState
        }
      },
      {
        field: 'isAWPM',
        label: t(translations.pages.internationalOffice.courses.isAWPM()),
        stateHandler: awpmState,
        switch: {
          label: t(translations.pages.internationalOffice.courses.isAWPM()),
          stateHandler: awpmState
        }
      }
    ];
  };

  const columns = useMemo((): TypedGridColumns<IOCourse & { semester: string }> => {
    return [
      {
        width: 110,
        headerName: t(translations.common.actions()),
        field: 'actions',
        type: 'actions',
        renderCell: params => {
          return enrolled(params.row.id) ? (
            <Button
              size="small"
              color="error"
              onClick={() => handleUnenroll(params.row.id)}
              variant="outlined"
              autoCapitalize="false"
              disabled={!isActive(semester, params.row.isWinter, params.row.isSummer)}
              sx={{ textTransform: 'none' }}
            >
              {t(translations.pages.internationalOffice.courses.unenroll())}
            </Button>
          ) : (
            <Button
              size="small"
              color="success"
              onClick={() => handleEnroll(params.row.id)}
              variant="outlined"
              disabled={!isActive(semester, params.row.isWinter, params.row.isSummer)}
              sx={{ textTransform: 'none' }}
            >
              {t(translations.pages.internationalOffice.courses.enroll())}
            </Button>
          );
        }
      },
      {
        flex: 2,
        minWidth: 250,
        headerName: t(translations.pages.internationalOffice.courses.faculty()),
        field: 'faculty',
        renderCell: params => (
          <Tooltip title={params.row.faculty}>
            <span>{params.row.faculty}</span>
          </Tooltip>
        )
      },
      {
        flex: 2,
        minWidth: 300,
        headerName: t(translations.pages.internationalOffice.courses.courseName()),
        field: 'courseName',
        renderCell: params => (
          <Tooltip title={params.row.courseName}>
            <span>{params.row.courseName}</span>
          </Tooltip>
        )
      },
      {
        flex: 1,
        minWidth: 150,
        headerName: t(translations.pages.internationalOffice.courses.courseOfficer()),
        field: 'courseOfficer',
        renderCell: params => (
          <Tooltip title={params.row.courseOfficer}>
            <span>{params.row.courseOfficer}</span>
          </Tooltip>
        )
      },
      {
        width: 120,
        headerName: t(translations.pages.internationalOffice.courses.semester()),
        field: 'semester',
        valueGetter: (value, row) => {
          if (row?.isSummer && row?.isWinter) {
            return `${t(translations.pages.internationalOffice.courses.isWinter())}/${t(
              translations.pages.internationalOffice.courses.isSummer()
            )}`;
          }
          if (row?.isSummer) {
            return t(translations.pages.internationalOffice.courses.isSummer());
          }
          if (row.isWinter) {
            return t(translations.pages.internationalOffice.courses.isWinter());
          }
          return '';
        }
      },
      {
        width: 100,
        headerName: t(translations.pages.internationalOffice.courses.degree()),
        field: 'degree'
      },
      {
        width: 100,
        type: 'boolean',
        headerName: t(translations.pages.internationalOffice.courses.isAWPMHeader()),
        field: 'isAWPM'
      },
      {
        width: 140,
        headerName: t(translations.pages.internationalOffice.courses.ects()),
        field: 'ectCount',
        align: 'center',
        headerAlign: 'center'
      },
      {
        width: 80,
        field: 'courseDescription',
        type: 'actions',
        align: 'center',
        headerName: t(translations.pages.internationalOffice.courses.courseDescription()),
        renderCell: (params: GridRenderCellParams) =>
          params.row.courseDescription ? (
            <a
              href={`${import.meta.env.VITE_GATEWAY_URL}/course/${params.row.id}/description`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon className="far fa-file-pdf fa-3x" />
            </a>
          ) : (
            <Icon
              className="fas fa-ban fa-3x"
              style={{
                color: '#aaa'
              }}
            />
          )
      }
    ];
  }, [t, enrolled, semester, handleUnenroll, handleEnroll]);

  useEffect(() => {
    refetch({ limit: paginationModel.pageSize });
  }, [paginationModel.pageSize, refetch]);

  return (
    <GQLGuard
      query={[coursesQuery, settingsQuery]}
      onError={error => {
        dispatch(
          AlertActions.error(
            `${t(translations.pages.internationalOffice.courses.alerts.error.loadingCourses())}`
          )
        );
        navigate('/', { replace: true });
        return null;
      }}
    >
      {([coursesData]) => {
        const coursesCount = coursesData?.ioCoursesCount;
        return (
          <DataGrid
            slotProps={{
              toolbar: {
                filters: {
                  filterConfig: filters(faculties, coursesData?.faculties),
                  onFilterChange: refetch
                },
                exportEnabled: showOnlyEnrolled ? true : false,
                printOptions: {
                  hideFooter: true,
                  hideToolbar: true,
                  bodyClassName: 'print-student-courses-table courses-management',
                  fields: [
                    'faculty',
                    'courseName',
                    'courseOfficer',
                    'semester',
                    'degree',
                    'isAWPM',
                    'ectCount'
                  ]
                }
              }
            }}
            rows={coursesData?.ioCourses || []}
            disableColumnFilter
            disableDensitySelector
            disableRowSelectionOnClick
            key={showOnlyEnrolled ? 'enrolled-table' : 'all-table'}
            columns={columns.map(column => ({
              ...column,
              sortable: false
            }))}
            getRowClassName={params =>
              clsx(
                !isActive(semester, params.row.isWinter, params.row.isSummer) && 'disabled',
                enrolled(params.id) && 'enrolled'
              )
            }
            paginationMode="server"
            // onPageSizeChange={handlePageSizeChange}
            // pageSize={pageSize}
            rowCount={coursesCount}
            paginationModel={{
              page: paginationModel.page,
              pageSize: paginationModel.pageSize
            }}
            onPaginationModelChange={pagModel => {
              handlePaginationChange(pagModel);
              handlePagination(null, pagModel.page);
            }}
            // onPageChange={(page, details) => handlePagination(details, page)}
            autoHeight
          />
        );
      }}
    </GQLGuard>
  );
};
